// URL helpers for the cross-tenant access grant (ADR-0023 §1, §3).
//
// A grant opens one tenant. It is sent only to our own api, and only on
// requests whose path is `/v1/tenants/{id}/...` with `{id}` matching the
// grant. The exceptions are the platform writes with no `{id}` (creating a
// clinic, refreshing the IDIS2GO customer list), which take the grant for
// the caller's own (platform) tenant. Callers first turn the request's
// final url into an api path with `apiPathFor`, so a request to any other
// host never matches.

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const TENANT_PATH = new RegExp(`^/v1/tenants/(${UUID})(?:/|$)`);
const GRANT_ROUTE = new RegExp(`^/v1/tenants/${UUID}/access-grants(?:/|$)`);

/**
 * Requests that create a secret and show it once. The SPA never repeats
 * these on its own, because a repeat would mint a second secret
 * (ADR-0023 §7). Every POST under `emr-instances` counts: registering an
 * instance (`POST .../emr-instances`) and rotating its key
 * (`POST .../emr-instances/{instanceId}/keys`).
 */
const NO_RETRY_ROUTES: readonly { method: string; path: RegExp }[] = [
  { method: 'post', path: new RegExp(`^/v1/tenants/${UUID}/emr-instances(?:/.*)?$`) },
];

/**
 * The request's path inside our api, or null when the url points anywhere
 * else. `fullUrl` is the final url (`axios.getUri(config)`). It must have
 * the api base's origin and sit under its base path.
 */
export function apiPathFor(fullUrl: string, apiBaseUrl: string): string | null {
  let base: URL;
  let target: URL;
  try {
    base = new URL(apiBaseUrl);
    target = new URL(fullUrl, base);
  } catch {
    return null;
  }
  if (target.origin !== base.origin) {
    return null;
  }
  const basePath = base.pathname.replace(/\/+$/, '');
  if (basePath === '') {
    return target.pathname;
  }
  if (target.pathname !== basePath && !target.pathname.startsWith(`${basePath}/`)) {
    return null;
  }
  const rest = target.pathname.slice(basePath.length);
  return rest === '' ? '/' : rest;
}

/** The tenant id in a `/v1/tenants/{id}/...` api path, lower case, or null. */
export function tenantIdFromPath(path: string | null): string | null {
  if (path === null) {
    return null;
  }
  const match = TENANT_PATH.exec(path);
  if (match === null) {
    return null;
  }
  return match[1]!.toLowerCase();
}

/** The grant routes themselves. They never carry a grant or ask for one. */
export function isGrantRoute(path: string | null): boolean {
  if (path === null) {
    return false;
  }
  return GRANT_ROUTE.test(path);
}

/**
 * Platform writes with no `{id}` in their path. Each is a write in the
 * caller's own tenant, the platform, so it takes the platform grant
 * (ADR-0023, the 2026-10-17 and 2026-10-18 amendments). Each may be sent
 * again after step-up: none makes a secret. A trailing slash is allowed.
 * - `POST /v1/tenants`: creating a clinic.
 * - `POST /v1/idis2go-customers/discovery-runs`: refreshing the IDIS2GO
 *   customer list. A repeat is safe: the api queues one run.
 */
const PLATFORM_WRITE_PATHS: readonly RegExp[] = [
  /^\/v1\/tenants\/?$/,
  /^\/v1\/idis2go-customers\/discovery-runs\/?$/,
];

/** A POST to one of the platform write paths above. */
export function isPlatformWriteRequest(method: string | undefined, path: string | null): boolean {
  if (path === null || method === undefined) {
    return false;
  }
  if (method.toLowerCase() !== 'post') {
    return false;
  }
  return PLATFORM_WRITE_PATHS.some((pattern) => {
    return pattern.test(path);
  });
}

/** True for an api request the SPA must never send twice on its own. */
export function isNoRetryRequest(method: string | undefined, path: string | null): boolean {
  if (path === null) {
    return false;
  }
  const verb = (method ?? 'get').toLowerCase();
  return NO_RETRY_ROUTES.some((route) => {
    return route.method === verb && route.path.test(path);
  });
}
